import { getSerialized } from '@extension/wa-communication-layer/core/utils/wa-key-serialization';
import { getWaChatContactDisplayName } from "@/common/utils/display-name-utils";
import { ExtendedUserMessage } from "@common/models/extend-user-message";

/**
 * #113 FE-1 — composer seed passthrough.
 *
 * A "seed" is an optional hint (from the first-week-value card / deep link)
 * that pre-fills the compose draft with a specific recipient, due date, and
 * a `guided` request-only flag (read by the backend as `ctx.data.guided`,
 * Task 6 — never persisted on the doc).
 */
export type ComposerSeed = {
  contactWid?: string;
  dueDate?: Date;
  guided?: boolean;
  // BE#657 M2 — a seed from a DELIBERATE user gesture (e.g. the first-week "send
  // yourself a test" / guided-workflow CTA) marks its seeded dueDate as user-chosen,
  // so Send is enabled without the user re-confirming the time in the picker.
  dueDateSetByUser?: boolean;
};

// `_id` is optional at this boundary (mirrors ScheduleMessageThunkArg in
// UserMessagesThunks.ts): a fresh draft carries no `_id` yet, so requiring
// the canonical ExtendedUserMessage's `_id: string` here would reject the
// exact "new draft" case this function exists to seed.
type DraftMessage = Omit<ExtendedUserMessage, "_id"> & { _id?: string };

/**
 * Pure. Applies an optional seed onto a draft message.
 *
 * - `seed.contactWid` set → the recipient becomes that single contact
 *   (`contactList = [{ id: seed.contactWid, name: '' }]`), overriding the
 *   current-chat-derived recipient.
 * - No seed / no `contactWid` → BEHAVIOR-PRESERVING: same current-chat
 *   contact resolution the thunk already did.
 * - `dueDate` → `seed.dueDate` when present, else the draft's own dueDate (which is
 *   `undefined` for a fresh draft). BE#657: it is NEVER defaulted to "now" — a near-now
 *   dueDate arms an immediate send before the user picks the intended future date.
 * - `guided` → `!!seed?.guided` (false with no seed — request-only field,
 *   never persisted).
 */
export function applySeedToDraft(
  message: DraftMessage,
  seed: ComposerSeed | undefined,
  currentChat: any,
): DraftMessage {
  const contactList = seed?.contactWid
    ? [{ id: seed.contactWid, name: "" }]
    : [
        {
          ...currentChat,
          id: getSerialized(currentChat?.id) ?? "",
          name: getWaChatContactDisplayName(currentChat),
        },
      ];

  return {
    ...message,
    contactList,
    // BE#657 — do NOT default an unseeded dueDate to "now". A near-now dueDate makes
    // an unfinished scheduled draft immediately sendable, so it can fire before the
    // user's intended (future) date is applied. Leave it unset; the compose Send gate
    // (canSchedule) already requires an explicit valid date before a send is allowed.
    dueDate: seed?.dueDate ?? message.dueDate,
    guided: !!seed?.guided,
    // BE#657 M2 — carry a deliberate-gesture seed's user-set marker onto the draft.
    ...(seed?.dueDateSetByUser ? { dueDateSetByUser: true } : {}),
  };
}
